import { useState } from 'react';
import { api, fmt, fmtDate, type QueueItem } from '../api.ts';

export interface ReceiveResult {
  id: number; jobNumber: string; quantity: number; wastage: number; stageNames: string;
  nextStage: { name: string; isDispatch: boolean } | null; jobComplete: boolean;
}

const today = () => new Date().toISOString().slice(0, 10);

/**
 * The goods are back: one step. The quantity that came back is what the later
 * stages work to; what is short of what was sent is wastage, worked out here
 * and never a second split.
 */
export default function ReceiveBackDialog({ item, onClose, onDone }: {
  item: QueueItem;
  onClose: () => void;
  onDone: (r: ReceiveResult) => void;
}) {
  const sent = Number(item.outside_sent_quantity);
  const worker = item.outside_worker ?? 'the job worker';
  const stages = item.outside_stage_names ?? item.stage_name;
  const [qty, setQty] = useState(String(sent));
  const [on, setOn] = useState(today());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const q = Number(qty);
  const whole = qty.trim() !== '' && Number.isInteger(q) && q > 0;
  const problem = !whole ? '' : q > sent ? `At most ${fmt(sent)} were sent` : '';
  const valid = whole && !problem && on !== '';
  const wastage = valid ? sent - q : 0;

  async function go() {
    if (!valid) return;
    setBusy(true); setError('');
    try {
      onDone(await api.post<ReceiveResult>(`/api/outside/${item.outside_id}/receive`, { quantity: q, receivedOn: on }));
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not receive it');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="backdrop" onClick={() => !busy && onClose()}>
      <div className="modal" style={{ width: 440 }} role="dialog" aria-label={`Receive from ${worker}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Receive from {worker}</h3>
          <p>{item.job_number} · {stages}</p>
        </div>
        <div className="modal-body">
          {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
          <div className="hint" style={{ marginBottom: 12 }}>
            {fmt(sent)} sent on {fmtDate(item.outside_sent_on)}
            {item.outside_expected_back ? ` · due back ${fmtDate(item.outside_expected_back)}` : ''}
          </div>
          <div className="field-pair">
            <div className="field">
              <label htmlFor="rb-qty">Received</label>
              <input id="rb-qty" type="number" min="1" step="1" autoFocus value={qty}
                     onFocus={(e) => e.target.select()} onChange={(e) => setQty(e.target.value)}
                     onKeyDown={(e) => { if (e.key === 'Enter') void go(); }} />
              {problem && <div className="hint pp-warn">{problem}</div>}
            </div>
            <div className="field">
              <label htmlFor="rb-on">Received on</label>
              <input id="rb-on" type="date" value={on} onChange={(e) => setOn(e.target.value)} />
            </div>
          </div>
          {valid && (
            <ul className="undo-list">
              <li>{wastage > 0 ? `Wastage ${fmt(wastage)} (${((wastage / sent) * 100).toFixed(1).replace(/\.0$/, '')}%), recorded automatically` : 'Nothing short'}</li>
              <li>{stages} {stages.includes('+') ? 'are' : 'is'} done, with {fmt(q)} as the quantity</li>
              {item.next_stage_name && <li>The job moves on to the next stage</li>}
            </ul>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={() => void go()} disabled={busy || !valid}>
            {busy ? 'Receiving…' : valid ? `Receive ${fmt(q)}` : 'Receive'}
          </button>
        </div>
      </div>
    </div>
  );
}
